/*
 * The reply agent's state on master-inbox rows (master-inbox plan, step 8d:
 * docs/2026-10-17-master-inbox-in-the-existing-ui-plan.md; the shell plan's S5). Apart from
 * `contract.ts` on purpose: the inbox's own shapes carry no agent material, and the gates test holds
 * them to it, so what the agent made travels only here, through a route that needs `reply_agent`.
 * Pure; browser readers derive tolerant schemas while server output remains strict.
 */
import { z } from "zod";
import { WorkPlanSchema } from "../../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { INBOX_PAGE_SIZE, InboxListQuerySchema, InboxPageSchema } from "./contract.ts";

/**
 * What a row says of the agent's work on it: a draft to use, a question it waits on the rep for, a
 * draft being written, a send in progress, an unknown send outcome to check in Bison, or no draft
 * where the rep owes an answer, or a phone lead ready to call. Null where the conversation asks
 * nothing of the rep. Ready to call leads the Status menu's Reply agent group, before Draft ready.
 */
const InboxAgentStateSchema = z.enum([
  "ready_to_call",
  "draft_ready",
  "needs_answer",
  "drafting",
  "no_draft",
  "sending",
  "check_bison",
]);
export type InboxAgentState = z.infer<typeof InboxAgentStateSchema>;

/** Exactly one `ids` parameter: validate its raw segments before deduplicating the page's ids. */
export const InboxAgentQuerySchema = z.strictObject({
  ids: z
    .tuple([
      z
        .string()
        .transform((value) => value.split(","))
        .pipe(z.array(WorkPlanSchema.shape.id).min(1).max(INBOX_PAGE_SIZE)),
    ])
    .transform(([ids]) => [...new Set(ids)]),
});

/** Each asked conversation found in the workspace; one of another workspace is left out. */
export const InboxAgentStatesSchema = z.strictObject({
  conversations: z.array(
    z.strictObject({
      id: z.string(),
      state: InboxAgentStateSchema.nullable(),
      /** Work can continue beneath a higher-priority chip, such as Ready to call. */
      workInFlight: z.boolean(),
    }),
  ),
});

/** Step 9: all agent filters, separate from master status and saved-view contracts. */
export const AgentStatusSchema = z.enum([
  ...InboxAgentStateSchema.options,
  "answer_owed",
  "agreed",
]);
export type AgentStatus = z.infer<typeof AgentStatusSchema>;

/**
 * GET /api/work/workspaces/:workspaceId/inbox/agent/list
 * Requires inbox_reply_agent. Master filters apply before agent status. Newest first only.
 * expand=list negotiates the same expanded rows as /inbox; limit is honored only with it.
 * agent is required; status=needs_reply|waiting remains an optional master filter.
 * Counts ignore agent but respect every master filter. Repeated agent/status is invalid_query.
 * offset pages the matches, never the candidates.
 */
export const InboxAgentListQuerySchema = InboxListQuerySchema.extend({
  agent: AgentStatusSchema,
  sort: z.literal("newest").default("newest"),
});
export type InboxAgentListQuery = z.infer<typeof InboxAgentListQuerySchema>;

/** Exact totals across all candidates admitted by the other filters, including zeroes. */
export const InboxAgentListSchema = z.strictObject({
  page: InboxPageSchema,
  counts: z.record(AgentStatusSchema, z.number().int().nonnegative()),
});
export type InboxAgentList = z.infer<typeof InboxAgentListSchema>;

/** HTTP 422; no partial page or approximate counts are returned. */
export const InboxAgentLimitErrorSchema = z.strictObject({
  error: z.literal("agent_status_candidate_limit"),
  limit: z.number().int().positive(),
  message: z.literal("Narrow by campaign or date to filter by agent state"),
});

/** HTTP 500: an internal accounting invariant failed; no partial results or automatic retry. */
export const InboxAgentAccountingErrorSchema = z.strictObject({
  error: z.literal("agent_status_accounting_mismatch"),
  reference: z.uuid(),
});
